import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AUTH } from "@/constants/testIds";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

function PasswordField({ label, value, onChange, testId, toggleTestId }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">{label}</label>
      <div className="relative">
        <input
          data-testid={testId}
          type={show ? "text" : "password"}
          required minLength={6}
          value={value} onChange={onChange}
          className="eo-input pr-10"
        />
        <button
          type="button"
          data-testid={toggleTestId}
          onClick={() => setShow((v) => !v)}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-[#5C5C54] hover:text-[#0A0A0A]"
          tabIndex={-1}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const { acceptInvite } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      if (!token) { setError("Missing invitation token"); return; }
      try {
        const { data } = await api.get(`/invites/verify/${token}`);
        setMeta(data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Invalid invitation");
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    const r = await acceptInvite(token, password);
    setBusy(false);
    if (!r.ok) { toast.error(r.error); return; }
    toast.success("Account created");
    nav("/");
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md eo-card p-8">
        <div className="font-mono-eo text-[10px] uppercase tracking-widest text-[#8A8A80]">// Invitation</div>
        <h1 className="font-heading text-[28px] font-bold mt-1">Accept your invitation</h1>
        {error && <div className="mt-4 border border-[#E63946] bg-[#FBECEE] p-3 text-[13px] text-[#7A1620]">{error}</div>}
        {meta && (
          <>
            <div className="mt-4 border border-[#DCD9D0] bg-[#FBFAF3] p-4 text-[13px]">
              <div><span className="text-[#8A8A80] font-mono-eo text-[11px] uppercase">Name</span> <b className="ml-2">{meta.name}</b></div>
              <div className="mt-1"><span className="text-[#8A8A80] font-mono-eo text-[11px] uppercase">Designation</span> <b className="ml-2">{meta.designation}</b></div>
              <div className="mt-1"><span className="text-[#8A8A80] font-mono-eo text-[11px] uppercase">Email</span> <b className="ml-2">{meta.email}</b></div>
            </div>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <PasswordField
                label="Set password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                testId={AUTH.invitePasswordInput}
                toggleTestId="invite-password-toggle"
              />
              <PasswordField
                label="Confirm password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                testId={AUTH.inviteConfirmInput}
                toggleTestId="invite-confirm-toggle"
              />
              <button data-testid={AUTH.inviteAcceptBtn} disabled={busy} className="eo-btn-primary w-full">{busy ? "Creating..." : "Create account & sign in"}</button>
            </form>
          </>
        )}
        <div className="mt-6 pt-6 border-t border-[#DCD9D0] text-[12px] text-[#8A8A80]">
          Already have an account? <Link to="/login" className="text-[#1D3557] underline">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
